<template>
  <div class="ai-chat-panel" ref="panel">
    <div
      class="panel-header"
      @mousedown="onHeaderMouseDown"
      @touchstart="onHeaderTouchStart"
    >
      <div class="panel-title-wrapper">
        <span
          class="panel-title"
          v-if="!isEditingTitle"
          @click="startEditTitle"
          title="点击修改标题"
          >{{ sessionTitle || "新对话" }}</span
        >
        <input
          class="panel-title-input"
          v-if="isEditingTitle"
          v-model="editedTitle"
          @keyup.enter="saveTitle"
          @keyup.esc="cancelEditTitle"
          @blur="saveTitle"
          ref="titleInput"
          :maxlength="50"
          placeholder="请输入标题"
        />
      </div>
      <div class="panel-header-actions">
        <span
          class="panel-action-btn"
          v-if="!isFullscreen"
          @click="$emit('fullscreen')"
          title="全屏"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="15 3 21 3 21 9"></polyline>
            <polyline points="9 21 3 21 3 15"></polyline>
            <line x1="21" y1="3" x2="14" y2="10"></line>
            <line x1="3" y1="21" x2="10" y2="14"></line>
          </svg>
        </span>
        <span
          class="panel-action-btn"
          v-if="isFullscreen"
          @click="$emit('windowed')"
          title="窗口化"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="4 14 10 14 10 20"></polyline>
            <polyline points="20 10 14 10 14 4"></polyline>
            <line x1="14" y1="10" x2="21" y2="3"></line>
            <line x1="3" y1="21" x2="10" y2="14"></line>
          </svg>
        </span>
        <span class="panel-close" @click="$emit('close')">&times;</span>
      </div>
    </div>
    <div class="panel-messages" ref="messages" @scroll="handleScroll">
      <div v-if="loadingMessages" class="loading-tip">加载中...</div>
      <template v-for="(msg, idx) in messages">
        <ai-message-bubble
          :key="msg.id || 'stream-' + idx"
          :message="msg"
          :isStreaming="
            isGenerating &&
            idx === messages.length - 1 &&
            msg.role === 'assistant'
          "
          :isSpeaking="currentSpeakingId === msg.id"
          @resend="handleResendMessage"
          @copied="handleCopySuccess"
          @copy-error="handleCopyError"
          @speak="handleSpeakMessage"
          @option-click="handleOptionClick"
        ></ai-message-bubble>
      </template>
      <div v-if="!loadingMessages && messages.length === 0" class="empty-tip">
        <svg viewBox="0 0 80 80" width="80" height="80">
          <defs>
            <linearGradient id="tipGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop
                offset="0%"
                style="stop-color: #2bbd6e; stop-opacity: 0.15"
              />
              <stop
                offset="100%"
                style="stop-color: #0d966a; stop-opacity: 0.08"
              />
            </linearGradient>
          </defs>
          <circle
            cx="40"
            cy="40"
            r="38"
            fill="url(#tipGrad)"
            stroke="#2BBD6E"
            stroke-width="1"
            stroke-opacity="0.3"
          />
          <text
            x="40"
            y="44"
            text-anchor="middle"
            fill="#2BBD6E"
            font-size="28"
          >
            灵机
          </text>
        </svg>
        <p>你好，我是百草健康灵机</p>
        <p class="sub">可以向我咨询中医药健康相关问题</p>
      </div>
      <div v-if="loadingMore" class="loading-tip">加载更多...</div>
    </div>
    <div class="panel-input-area">
      <div class="image-preview" v-if="selectedImage">
        <img :src="selectedImagePreview" />
        <span class="remove-image" @click="removeImage">&times;</span>
      </div>
      <div class="input-row">
        <!-- <label class="upload-btn" title="上传图片">
          <input type="file" accept="image/*" @change="handleImageSelect" style="display:none" ref="fileInput" />
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#6B7280" stroke-width="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
        </label> -->
        <button
          class="voice-input-btn"
          :class="{ recording: isRecording }"
          @click="toggleVoiceInput"
          :title="isRecording ? '停止录音' : '语音输入，谷歌浏览器暂不支持'"
        >
          <svg
            v-if="!isRecording"
            viewBox="0 0 24 24"
            width="22"
            height="22"
            fill="none"
            stroke="#6B7280"
            stroke-width="2"
          >
            <path
              d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"
            ></path>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
            <line x1="12" y1="19" x2="12" y2="23"></line>
            <line x1="8" y1="23" x2="16" y2="23"></line>
          </svg>
          <svg
            v-else
            viewBox="0 0 24 24"
            width="22"
            height="22"
            fill="#EF4444"
            stroke="#EF4444"
            stroke-width="2"
          >
            <path
              d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"
            ></path>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
            <line x1="12" y1="19" x2="12" y2="23"></line>
            <line x1="8" y1="23" x2="16" y2="23"></line>
          </svg>
        </button>
        <textarea
          class="msg-input"
          ref="msgInput"
          v-model="inputText"
          :placeholder="
            isGenerating
              ? '灵机正在回复中...'
              : isRecording
              ? '正在聆听...'
              : '输入消息...（Shift+Enter换行）'
          "
          :disabled="isGenerating || isRecording"
          @keydown.enter.exact.prevent="sendMessage"
          @input="autoResizeTextarea"
          rows="1"
        ></textarea>
        <button
          v-if="!isGenerating"
          class="send-btn"
          :disabled="!canSend"
          @click="sendMessage"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
          </svg>
        </button>
        <button v-else class="stop-btn" @click="stopGenerating">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="white">
            <rect x="6" y="6" width="12" height="12" rx="2" />
          </svg>
          停止
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import AiMessageBubble from "./AiMessageBubble.vue";
import { EventBus } from "../../js/eventbus.js";
import { markdownToPlainText } from "../../js/markdown.js";
export default {
  name: "AiChatPanel",
  components: { AiMessageBubble },
  props: {
    sessionId: { type: String, default: "" },
    sessionTitle: { type: String, default: "" },
    isFullscreen: { type: Boolean, default: false },
  },
  data() {
    return {
      messages: [],
      inputText: "",
      isGenerating: false,
      loadingMessages: false,
      loadingMore: false,
      hasMore: false,
      abortController: null,
      selectedImage: null,
      selectedImagePreview: "",
      uploadedImageUrl: "",
      isEditingTitle: false,
      editedTitle: "",
      isRecording: false,
      recognition: null,
      speechSynthesis: null,
      currentUtterance: null,
      currentSpeakingId: null,
      pendingSendText: '',          // 延迟创建会话时的待发送文本
      pendingSendImageUrl: '',      // 延迟创建会话时的待发送图片
      requestSeq: 0,                // 发送请求序号，用于丢弃已失效请求的回调
    };
  },
  computed: {
    canSend() {
      return (
        (this.inputText.trim().length > 0 || this.uploadedImageUrl) &&
        !this.isGenerating
      );
    },
  },
  watch: {
    sessionId(newVal) {
      if (newVal) {
        this.messages = [];
        this.hasMore = false;
        var hasPending = !!(this.pendingSendText || this.pendingSendImageUrl);
        // 新建会话首条消息待发送时，不再拉取历史消息，避免异步结果覆盖本地消息
        if (!hasPending) {
          this.loadMessages();
        } else {
          this.loadingMessages = false;
        }
        // 检查是否有待发送的内容（延迟创建会话场景）
        if (hasPending) {
          var content = this.pendingSendText;
          var imageUrl = this.pendingSendImageUrl;
          this.pendingSendText = '';
          this.pendingSendImageUrl = '';
          this.inputText = content;
          this.uploadedImageUrl = imageUrl;
          this.$nextTick(function () {
            this.sendMessage();
          });
        }
      } else {
        this.messages = [];
        this.hasMore = false;
        this.loadingMessages = false;
      }
    },
  },
  mounted() {
    // 监听AI输入文本设置事件
    EventBus.$on("set-ai-input-text", (text) => {
      this.inputText = text;
      this.$nextTick(() => {
        const input = this.$refs.msgInput;
        if (input) {
          input.focus();
          this.autoResizeTextarea();
        }
        // 自动发送消息（无session时会触发延迟创建）
        this.sendMessage();
      });
    });

    // 初始化语音识别
    this.initSpeechRecognition();

    // 初始化语音合成
    this.initSpeechSynthesis();

    // 监听网络状态变化
    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);
  },
  beforeDestroy() {
    // 移除事件监听器
    EventBus.$off("set-ai-input-text");

    // 停止语音识别
    if (this.recognition) {
      this.recognition.abort();
      this.recognition = null;
    }

    // 停止语音合成
    if (this.speechSynthesis) {
      this.speechSynthesis.cancel();
    }

    // 移除网络状态监听
    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("offline", this.handleOffline);
  },
  methods: {
    onHeaderMouseDown(event) {
      // 如果正在编辑标题，不允许拖动
      if (this.isEditingTitle) {
        return;
      }
      // 全屏模式下不允许拖动
      if (this.isFullscreen) {
        return;
      }
      // 排除操作按钮区域和标题输入框
      if (
        event.target.closest(".panel-header-actions") ||
        event.target.closest(".panel-title-input")
      ) {
        return;
      }
      // 向父组件发送拖动开始事件
      this.$emit("start-drag", event);
    },
    onHeaderTouchStart(event) {
      // 如果正在编辑标题，不允许拖动
      if (this.isEditingTitle) {
        return;
      }
      // 全屏模式下不允许拖动
      if (this.isFullscreen) {
        return;
      }
      // 排除操作按钮区域和标题输入框
      if (
        event.target.closest(".panel-header-actions") ||
        event.target.closest(".panel-title-input")
      ) {
        return;
      }
      // 向父组件发送拖动开始事件
      this.$emit("start-drag", event);
    },
    initSpeechRecognition() {
      // 检查浏览器支持
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        console.warn("当前浏览器不支持语音识别功能");
        return;
      }

      this.recognition = new SpeechRecognition();
      this.recognition.continuous = false; // 非连续模式，说完一句话自动停止
      this.recognition.interimResults = true; // 显示临时结果
      this.recognition.lang = "zh-CN"; // 设置为中文

      this.recognition.onstart = () => {
        console.log("语音识别已启动");
        this.isRecording = true;
        this.$message({ message: "开始录音，请说话...", type: "info" });
      };

      this.recognition.onresult = (event) => {
        let finalTranscript = "";
        let interimTranscript = "";

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        // 如果有最终结果，添加到输入框
        if (finalTranscript) {
          this.inputText += finalTranscript;
          this.$nextTick(() => {
            this.autoResizeTextarea();
          });
        }
      };

      this.recognition.onerror = (event) => {
        console.error("语音识别错误:", event.error, event);
        let errorMsg = "语音识别失败";

        switch (event.error) {
          case "no-speech":
            errorMsg = "未检测到语音，请重试";
            break;
          case "audio-capture":
            errorMsg = "无法访问麦克风，请检查设备";
            break;
          case "not-allowed":
            errorMsg = "麦克风权限被拒绝，请在浏览器设置中允许";
            break;
          case "network":
            errorMsg = "网络连接失败，请检查网络后重试";
            console.warn("语音识别网络错误，可能是：");
            console.warn("1. 网络连接不稳定");
            console.warn("2. 防火墙阻止了语音服务");
            console.warn("3. 浏览器不支持离线语音识别");
            break;
          case "aborted":
            // 用户主动中止，不显示错误
            console.log("语音识别已中止");
            this.isRecording = false;
            return;
          case "service-not-allowed":
            errorMsg = "语音识别服务不可用";
            break;
          case "bad-request":
            errorMsg = "语音识别请求错误";
            break;
          case "language-not-supported":
            errorMsg = "不支持的语言";
            break;
          default:
            errorMsg = `语音识别错误: ${event.error}`;
        }

        this.$message({ message: errorMsg, type: "error", duration: 3000 });
        this.isRecording = false;
      };

      this.recognition.onend = () => {
        console.log("语音识别已结束");
        this.isRecording = false;
      };
    },

    initSpeechSynthesis() {
      this.speechSynthesis = window.speechSynthesis;
    },

    toggleVoiceInput() {
      if (!this.recognition) {
        this.$message({
          message: "当前浏览器不支持语音输入功能",
          type: "warning",
        });
        return;
      }

      if (this.isRecording) {
        this.stopVoiceInput();
      } else {
        this.startVoiceInput();
      }
    },

    startVoiceInput() {
      if (!this.recognition) {
        this.$message({ message: "语音识别未初始化", type: "error" });
        return;
      }

      // 如果已经在录音或正在启动中，不重复启动
      if (this.isRecording) {
        console.warn("语音识别已在运行中");
        return;
      }

      // 检查网络连接
      if (!navigator.onLine) {
        this.$message({
          message: "当前处于离线状态，语音识别需要网络连接",
          type: "warning",
          duration: 3000,
        });
        return;
      }

      try {
        this.recognition.start();
      } catch (err) {
        console.error("启动语音识别失败:", err);
        // 如果是已经启动的错误，忽略它
        if (err.name === "InvalidStateError") {
          console.warn("语音识别已经启动");
          this.isRecording = true;
        } else {
          this.$message({
            message: "启动语音识别失败，请刷新页面重试",
            type: "error",
            duration: 3000,
          });
        }
      }
    },

    stopVoiceInput() {
      if (!this.recognition) {
        return;
      }

      try {
        this.recognition.stop();
        console.log("语音识别已手动停止");
      } catch (err) {
        console.error("停止语音识别失败:", err);
      } finally {
        // 无论成功与否，都重置状态
        this.isRecording = false;
      }
    },

    handleSpeakMessage(message) {
      console.log("handleSpeakMessage", message);
      if (!this.speechSynthesis) {
        this.$message({
          message: "当前浏览器不支持语音朗读功能",
          type: "warning",
        });
        return;
      }

      // 如果点击的是当前正在朗读的消息，则停止朗读
      if (
        this.currentSpeakingId === message.id &&
        this.speechSynthesis.speaking
      ) {
        this.speechSynthesis.cancel();
        this.currentSpeakingId = null;
        this.currentUtterance = null;
        return;
      }

      // 如果正在朗读其他消息，先停止
      if (this.speechSynthesis.speaking) {
        this.speechSynthesis.cancel();
      }

      var text = markdownToPlainText(message.content);
      if (!text.trim()) {
        this.$message({ message: "没有可朗读的内容", type: "warning" });
        return;
      }

      // 创建语音实例
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = "zh-CN";
      utterance.rate = 1.0;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;

      // 设置当前朗读的消息ID
      this.currentSpeakingId = message.id;

      utterance.onstart = () => {
        this.currentUtterance = utterance;
      };

      utterance.onend = () => {
        this.currentUtterance = null;
        this.currentSpeakingId = null;
      };

      utterance.onerror = (event) => {
        console.error("语音朗读错误:", event);
        // this.$message({ message: '语音朗读失败', type: 'error' })
        this.currentUtterance = null;
        this.currentSpeakingId = null;
      };

      // 开始朗读
      this.speechSynthesis.speak(utterance);
    },

    handleOnline() {
      console.log("网络已连接");
      this.$message({
        message: "网络已恢复，可以使用语音输入",
        type: "success",
        duration: 2000,
      });
    },

    handleOffline() {
      console.log("网络已断开");
      this.$message({
        message: "网络已断开，语音输入暂时不可用",
        type: "warning",
        duration: 3000,
      });
      // 如果正在录音，停止它
      if (this.isRecording) {
        this.stopVoiceInput();
      }
    },

    loadMessages() {
      if (!this.sessionId) return;
      this.loadingMessages = true;
      this.wjtHttp.get(
        this,
        "/api/ai/chat/session/" + this.sessionId + "/messages",
        { pageSize: 20 },
        false,
        (res) => {
          this.loadingMessages = false;
          if (res.data.code === 200) {
            this.messages = res.data.data || [];
            this.hasMore = this.messages.length >= 20;
            this.$nextTick(() => {
              this.scrollToBottom(false);
            });
          }
        },
        () => {
          this.loadingMessages = false;
        }
      );
    },
    loadMoreMessages() {
      if (!this.hasMore || this.loadingMore || this.messages.length === 0)
        return;
      var firstMsgId = this.messages[0].id;
      this.loadingMore = true;
      this.wjtHttp.get(
        this,
        "/api/ai/chat/session/" + this.sessionId + "/messages/more",
        { lastMessageId: firstMsgId, pageSize: 20 },
        false,
        (res) => {
          this.loadingMore = false;
          if (res.data.code === 200) {
            var older = res.data.data || [];
            this.hasMore = older.length >= 20;
            this.messages = older.concat(this.messages);
          }
        },
        () => {
          this.loadingMore = false;
        }
      );
    },
    handleScroll() {
      var el = this.$refs.messages;
      if (!el) return;
      if (el.scrollTop <= 30) {
        this.loadMoreMessages();
      }
    },
    scrollToBottom(smooth) {
      var el = this.$refs.messages;
      if (!el) return;
      this.$nextTick(function () {
        if (smooth) {
          el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
        } else {
          el.scrollTop = el.scrollHeight;
        }
      });
    },
    autoResizeTextarea() {
      var textarea = this.$refs.msgInput;
      if (!textarea) return;
      // 重置高度以获取正确的scrollHeight
      textarea.style.height = "auto";
      // 设置新高度（最大120px，约4行）
      var newHeight = Math.min(textarea.scrollHeight, 120);
      textarea.style.height = newHeight + "px";
    },
    sendMessage() {
      if (!this.canSend) return;

      // 如果没有 sessionId，保存待发送内容并通知父组件创建会话（延迟创建）
      if (!this.sessionId) {
        this.pendingSendText = this.inputText.trim();
        this.pendingSendImageUrl = this.uploadedImageUrl || '';
        this.inputText = '';
        this.$emit('request-create-session');
        return;
      }

      var seq = ++this.requestSeq;
      var vm = this;
      var content = this.inputText.trim();
      var imageUrl = this.uploadedImageUrl || "";
      this.inputText = "";
      // 清空输入框后重置高度
      this.$nextTick(() => {
        var textarea = this.$refs.msgInput;
        if (textarea) {
          textarea.style.height = "auto";
        }
      });
      this.messages.push({
        id: "temp-user-" + Date.now(),
        sessionId: this.sessionId,
        role: "user",
        content: content,
        messageType: imageUrl ? "image" : "text",
        imageUrl: imageUrl,
        createTime: new Date(),
      });
      this.removeImage();
      this.scrollToBottom(true);
      this.isGenerating = true;

      // 添加AI助手的加载提示消息
      var loadingMsgId = "loading-" + Date.now();
      this.messages.push({
        id: loadingMsgId,
        sessionId: this.sessionId,
        role: "assistant",
        content: "灵机正在思考...",
        messageType: "text",
        createTime: new Date(),
        isLoading: true,
      });
      this.scrollToBottom(true);

      var controller = new AbortController();
      this.abortController = controller;
      var token = this.$cookies.get("token");
      var headers = { "Content-Type": "application/json" };
      if (token && token.length > 0) {
        headers["Authorization"] = token;
      }
      fetch("/api/ai/chat/session/" + this.sessionId + "/send", {
        method: "POST",
        headers: headers,
        body: JSON.stringify({ content: content, imageUrl: imageUrl }),
        signal: controller.signal,
      })
        .then(function (response) {
          if (seq !== vm.requestSeq) return;
          if (!response.ok) {
            throw new Error("请求失败");
          }
          // 移除加载提示消息
          var loadingIndex = vm.messages.findIndex(function (msg) {
            return msg.id === loadingMsgId;
          });
          if (loadingIndex !== -1) {
            vm.messages.splice(loadingIndex, 1);
          }
          return vm.readSSEStream(response.body.getReader());
        })
        .then(function () {
          // 请求已失效（如中途开启了新对话），忽略回调
          if (seq !== vm.requestSeq) return;
          vm.isGenerating = false;
          if (vm.abortController === controller) {
            vm.abortController = null;
          }
          vm.$emit("refresh-sessions");
        })
        .catch(function (err) {
          // 请求已失效（如中途开启了新对话），忽略错误与提示
          if (seq !== vm.requestSeq) return;
          // 移除加载提示消息
          var loadingIndex = vm.messages.findIndex(function (msg) {
            return msg.id === loadingMsgId;
          });
          if (loadingIndex !== -1) {
            vm.messages.splice(loadingIndex, 1);
          }

          if (err.name === "AbortError") {
            vm.messages.push({
              id: "stop-" + Date.now(),
              sessionId: vm.sessionId,
              role: "assistant",
              content: "[已停止生成]",
              messageType: "text",
              createTime: new Date(),
            });
          } else {
            vm.messages.push({
              id: "error-" + Date.now(),
              sessionId: vm.sessionId,
              role: "assistant",
              content: "抱歉，服务暂时不可用，请稍后重试。",
              messageType: "text",
              createTime: new Date(),
            });
          }
          vm.isGenerating = false;
          if (vm.abortController === controller) {
            vm.abortController = null;
          }
          vm.scrollToBottom(true);
        });
    },
    // 复位为新对话状态：清空消息、输入与待发送内容，并终止旧会话的生成
    resetForNewSession() {
      // 序号自增，使正在进行的请求及其回调全部失效
      this.requestSeq++;
      var oldSessionId = this.sessionId;
      var wasGenerating = this.isGenerating;
      if (this.abortController) {
        try {
          this.abortController.abort();
        } catch (e) {}
        this.abortController = null;
      }
      if (wasGenerating && oldSessionId) {
        this.wjtHttp.post(
          this,
          "/api/ai/chat/session/" + oldSessionId + "/cancel",
          {},
          false,
          function () {},
          function () {}
        );
      }
      this.isGenerating = false;
      this.messages = [];
      this.hasMore = false;
      this.loadingMessages = false;
      this.loadingMore = false;
      this.inputText = "";
      this.pendingSendText = "";
      this.pendingSendImageUrl = "";
      this.removeImage();
      this.$nextTick(() => {
        var textarea = this.$refs.msgInput;
        if (textarea) {
          textarea.style.height = "auto";
        }
      });
    },
    readSSEStream(reader) {
      var vm = this;
      var decoder = new TextDecoder();
      var buffer = "";
      var aiMsg = {
        id: "stream-" + Date.now(),
        sessionId: vm.sessionId,
        role: "assistant",
        content: "",
        messageType: "text",
        createTime: new Date(),
      };
      vm.messages.push(aiMsg);
      var msgIndex = vm.messages.length - 1;
      var currentEvent = null;

      // 逐字输出优化：字符队列与定时显示
      var charQueue = []; // 待显示的字符队列
      var isTypingActive = false; // 打字机是否正在运行
      var typingTimer = null; // 打字机定时器
      var lastScrollTime = 0;
      var SCROLL_THROTTLE_MS = 100; // 滚动节流时间
      var TYPING_SPEED_MS = 30; // 打字速度：每个字符30ms（约33字/秒），更快的流式体验
      var BATCH_SIZE = 3; // 每批处理的字符数，提高流畅度

      // 启动打字机效果
      function startTyping() {
        if (isTypingActive) return;
        isTypingActive = true;
        typingTimer = setInterval(typeNextBatch, TYPING_SPEED_MS);
      }

      // 停止打字机效果
      function stopTyping() {
        if (typingTimer) {
          clearInterval(typingTimer);
          typingTimer = null;
        }
        isTypingActive = false;
      }

      // 处理下一批字符
      function typeNextBatch() {
        if (!vm.messages[msgIndex] || charQueue.length === 0) {
          // 队列为空，停止打字机
          stopTyping();
          return;
        }

        var batchContent = "";
        var batchCount = Math.min(BATCH_SIZE, charQueue.length);

        for (var i = 0; i < batchCount; i++) {
          batchContent += charQueue.shift();
        }

        // 更新消息内容
        vm.messages[msgIndex].content += batchContent;
        vm.$set(
          vm.messages,
          msgIndex,
          Object.assign({}, vm.messages[msgIndex])
        );

        // 节流滚动
        var now = Date.now();
        if (now - lastScrollTime > SCROLL_THROTTLE_MS) {
          vm.scrollToBottom(false);
          lastScrollTime = now;
        }

        // 如果队列已空，停止打字机
        if (charQueue.length === 0) {
          stopTyping();
        }
      }

      // 将内容添加到字符队列
      function addToQueue(content) {
        if (!content || !vm.messages[msgIndex]) return;

        // 将内容拆分为单个字符
        for (var i = 0; i < content.length; i++) {
          charQueue.push(content.charAt(i));
        }

        // 如果有内容且打字机未启动，则启动打字机
        if (charQueue.length > 0 && !isTypingActive) {
          startTyping();
        }
      }

      // 立即显示所有队列中的字符（用于完成或错误时）
      function flushQueue() {
        if (charQueue.length === 0 || !vm.messages[msgIndex]) return;

        var remainingContent = charQueue.join("");
        vm.messages[msgIndex].content += remainingContent;
        vm.$set(
          vm.messages,
          msgIndex,
          Object.assign({}, vm.messages[msgIndex])
        );
        charQueue = [];
        vm.scrollToBottom(true);
        stopTyping();
      }

      function processLine(line) {
        line = line.trim();
        if (line.startsWith("event:")) {
          currentEvent = line.substring(6).trim();
          // 如果是done或error事件，立即处理
          if (currentEvent === "done") {
            flushQueue();
            if (vm.messages[msgIndex]) {
              vm.messages[msgIndex].id = "done-" + Date.now();
            }
            return "stop";
          } else if (currentEvent === "error") {
            flushQueue();
            if (vm.messages[msgIndex]) {
              vm.messages[msgIndex].content += "\n[服务异常]";
              vm.messages[msgIndex].id = "err-" + Date.now();
              vm.$set(
                vm.messages,
                msgIndex,
                Object.assign({}, vm.messages[msgIndex])
              );
            }
            return "stop";
          }
        } else if (line.startsWith("data:")) {
          var data = line.substring(5).trim();
          if (!data || data === '""' || data === "[DONE]") {
            return;
          }
          // 根据当前事件类型处理数据
          try {
            var parsed = JSON.parse(data);
            var content = "";
            if (parsed.content) {
              content = parsed.content;
            } else if (
              parsed.choices &&
              Array.isArray(parsed.choices) &&
              parsed.choices[0]
            ) {
              var choice = parsed.choices[0];
              if (choice.delta && choice.delta.content) {
                content = choice.delta.content;
              } else if (choice.text) {
                content = choice.text;
              } else if (choice.message && choice.message.content) {
                content = choice.message.content;
              }
            } else if (typeof parsed === "string") {
              content = parsed;
            } else {
              content = JSON.stringify(parsed);
            }
            if (content && vm.messages[msgIndex]) {
              addToQueue(content);
            }
          } catch (e) {
            // 不是JSON，直接作为文本
            if (data && vm.messages[msgIndex]) {
              addToQueue(data);
            }
          }
        }
        return "continue";
      }

      function read() {
        return reader.read().then(function (result) {
          if (result.done) {
            flushQueue();
            if (vm.messages[msgIndex]) {
              vm.messages[msgIndex].id = "done-" + Date.now();
            }
            return;
          }
          buffer += decoder.decode(result.value, { stream: true });
          var lines = buffer.split("\n");
          buffer = lines.pop() || "";
          for (var i = 0; i < lines.length; i++) {
            var line = lines[i];
            var status = processLine(line);
            if (status === "stop") {
              stopTyping();
              return;
            }
          }
          return read();
        });
      }

      return read();
    },
    stopGenerating() {
      if (this.abortController) {
        this.abortController.abort();
      }
      this.wjtHttp.post(
        this,
        "/api/ai/chat/session/" + this.sessionId + "/cancel",
        {},
        false,
        function (res) {},
        function () {}
      );
    },
    handleImageSelect(e) {
      var file = e.target.files[0];
      if (!file) return;
      if (file.size / 1024 / 1024 > 5) {
        this.$message({ message: "图片大小不能超过5MB", type: "warning" });
        return;
      }
      this.selectedImage = file;
      this.selectedImagePreview = URL.createObjectURL(file);
      this.uploadImage(file);
      e.target.value = "";
    },
    uploadImage(file) {
      var vm = this;
      var token = this.$cookies.get("token");
      var formData = new FormData();
      formData.append("file", file);
      var xhr = new XMLHttpRequest();
      xhr.open("POST", "/api/common/upload");
      if (token) {
        xhr.setRequestHeader("Authorization", token);
      }
      xhr.onload = function () {
        if (xhr.status === 200) {
          var res = JSON.parse(xhr.responseText);
          if (res.code === 200) {
            vm.uploadedImageUrl = res.url;
          } else {
            vm.$message({ message: res.msg || "上传失败", type: "error" });
            vm.removeImage();
          }
        } else {
          vm.$message({ message: "上传失败", type: "error" });
          vm.removeImage();
        }
      };
      xhr.onerror = function () {
        vm.$message({ message: "上传失败", type: "error" });
        vm.removeImage();
      };
      xhr.send(formData);
    },
    removeImage() {
      this.selectedImage = null;
      this.selectedImagePreview = "";
      this.uploadedImageUrl = "";
    },
    handleResendMessage(message) {
      // 重发消息：将消息内容设置到输入框并发送
      if (!message || this.isGenerating) return;

      // 如果是图片消息，需要重新上传图片吗？这里简化处理，只重发文本
      if (message.messageType === "image" && message.imageUrl) {
        // 图片消息：直接使用原图片URL
        this.inputText = message.content || "";
        this.uploadedImageUrl = message.imageUrl;
      } else {
        // 文本消息
        this.inputText = message.content || "";
        this.uploadedImageUrl = "";
      }

      // 自动发送
      this.$nextTick(() => {
        this.sendMessage();
      });
    },
    handleCopySuccess(message) {
      // 显示复制成功提示
      if (this.$message) {
        this.$message({
          message: message || "消息已复制到剪贴板",
          type: "success",
        });
      } else {
        console.log(message || "消息已复制到剪贴板");
      }
    },
    handleCopyError(message) {
      // 显示复制失败提示
      if (this.$message) {
        this.$message({
          message: message || "复制失败，请重试",
          type: "error",
        });
      } else {
        console.error(message || "复制失败，请重试");
      }
    },
    startEditTitle() {
      if (!this.sessionId || this.isGenerating) return;
      this.isEditingTitle = true;
      this.editedTitle = this.sessionTitle || "";
      this.$nextTick(() => {
        const input = this.$refs.titleInput;
        if (input) {
          input.focus();
          input.select();
        }
      });
    },
    saveTitle() {
      if (!this.isEditingTitle || !this.sessionId) return;

      const title = this.editedTitle.trim();
      if (title === this.sessionTitle || title.length === 0) {
        this.cancelEditTitle();
        return;
      }

      this.wjtHttp.put(
        this,
        `/api/ai/chat/session/${this.sessionId}/title`,
        { title: title },
        false,
        (res) => {
          if (res.data.code === 200) {
            this.$emit("title-updated", title);
            this.$message({ message: "标题已更新", type: "success" });
            this.isEditingTitle = false;
          } else {
            this.$message({
              message: res.data.msg || "更新失败",
              type: "error",
            });
          }
        },
        (error) => {
          console.error("更新标题失败:", error);
          this.$message({ message: "更新失败，请重试", type: "error" });
        }
      );
    },
    cancelEditTitle() {
      this.isEditingTitle = false;
      this.editedTitle = "";
    },
    handleOptionClick(optText) {
      if (this.isGenerating) return;
      this.inputText = optText;
      this.$nextTick(function () {
        this.autoResizeTextarea();
        this.sendMessage();
      });
    },
  },
};
</script>

<style scoped>
.ai-chat-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f7f8fa;
}
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  cursor: move;
  user-select: none;
}
.panel-header:active {
  cursor: grabbing;
}
.panel-title {
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
}
.panel-close {
  font-size: 28px;
  color: #9ca3af;
  cursor: pointer;
  line-height: 1;
  transition: color 0.2s;
}
.panel-close:hover {
  color: #ef4444;
}
.panel-header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.panel-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  color: #9ca3af;
  cursor: pointer;
  border-radius: 6px;
  transition: color 0.2s, background 0.2s;
}
.panel-action-btn:hover {
  color: #2bbd6e;
  background: rgba(43, 189, 110, 0.1);
}
.panel-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px 8px;
  scroll-behavior: auto;
  word-break: break-word;
  overflow-wrap: break-word;
}
.panel-messages::-webkit-scrollbar {
  width: 4px;
}
.panel-messages::-webkit-scrollbar-track {
  background: transparent;
}
.panel-messages::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 4px;
}
.loading-tip {
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
  padding: 12px;
}
.empty-tip {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #6b7280;
}
.empty-tip p {
  margin: 8px 0 0;
  font-size: 15px;
  font-weight: 500;
}
.empty-tip .sub {
  font-size: 13px;
  color: #9ca3af;
  margin-top: 4px;
}
.panel-input-area {
  padding: 10px 12px 12px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}
.image-preview {
  position: relative;
  display: inline-block;
  margin-bottom: 8px;
}
.image-preview img {
  max-width: 80px;
  max-height: 80px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}
.remove-image {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  background: #ef4444;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;
  line-height: 1;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}
.input-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.msg-input {
  flex: 1;
  min-height: 38px;
  max-height: 120px;
  padding: 8px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  font-size: 14px;
  font-family: inherit;
  line-height: 1.5;
  outline: none;
  background: #f9fafb;
  transition: border-color 0.2s, background 0.2s;
  resize: none;
  overflow-y: auto;
}
.msg-input:focus {
  border-color: #2bbd6e;
  background: #fff;
}
.msg-input:disabled {
  background: #f3f4f6;
  color: #9ca3af;
  cursor: not-allowed;
}
.upload-btn {
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 4px;
  border-radius: 6px;
  transition: background 0.2s;
}
.upload-btn:hover {
  background: #f3f4f6;
}
.voice-input-btn {
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s;
  border: none;
  background: transparent;
}
.voice-input-btn:hover {
  background: #f3f4f6;
}
.voice-input-btn.recording {
  background: rgba(239, 68, 68, 0.1);
  animation: pulse-recording 1.5s infinite;
}
@keyframes pulse-recording {
  0% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(239, 68, 68, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
  }
}
.send-btn {
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 50%;
  background: linear-gradient(135deg, #2bbd6e, #1da85c);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s, opacity 0.2s;
  flex-shrink: 0;
  padding: 0;
}
.send-btn:hover {
  transform: scale(1.05);
}
.send-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  transform: none;
}
.stop-btn {
  height: 38px;
  padding: 0 14px;
  border: none;
  border-radius: 20px;
  background: #ef4444;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  flex-shrink: 0;
  transition: background 0.2s;
}
.stop-btn:hover {
  background: #dc2626;
}

.panel-title-wrapper {
  flex: 1;
  min-width: 0;
}
.panel-title {
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
  cursor: pointer;
  transition: color 0.2s;
  padding: 2px 4px;
  border-radius: 4px;
}
.panel-title:hover {
  color: #2bbd6e;
  background: rgba(43, 189, 110, 0.1);
}
.panel-title-input {
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  border: 1px solid #2bbd6e;
  border-radius: 4px;
  padding: 2px 6px;
  outline: none;
  width: 100%;
  max-width: 200px;
  background: #fff;
}
.panel-title-input:focus {
  border-color: #1da85c;
  box-shadow: 0 0 0 2px rgba(43, 189, 110, 0.2);
}
</style>
